/**
 * MarketingManual — /#/manuals/marketing
 * Long-scroll, well-typeset HTML manual mirroring the Marketing PDF.
 *
 * Brand locks: Navy #0B0B0F · Red #DC2626 · AIP bg #FAFAF7.
 * No exclamation points. No emojis. Never "rep".
 *
 * W66.X4 — rewritten to match current production:
 *   - Pipeline Reality Strip bucket labels corrected: COMMIT · UPSIDE · OMITTED · WON.
 *     NEW and PROBABLE were never canonical buckets and have been removed.
 *   - "Marketing is the gate" replaced with "Marketing holds approval authority" (banned term fix).
 *   - MarketingHome (W55) walkthrough updated to reflect actual component order and labels.
 *   - MQL pacing example numbers removed from body copy; description references the live counter.
 *   - Helix glossary route clarified.
 */

import ManualLayout, {
  ManualSectionHeading,
  Callout,
  ManualImage,
  StepList,
  BulletList,
  ManualPara,
  ManualTable,
  type ManualSection,
} from "@/components/manuals/ManualLayout";

import shot01 from "@/assets/manuals/marketing/01-dashboard-marketing.png";
import shot02 from "@/assets/manuals/marketing/02-marketing-plan-grid.png";
import shot03 from "@/assets/manuals/marketing/03-marketing-calendar.png";
import shot04 from "@/assets/manuals/marketing/04-marketing-hooks.png";
import shot05 from "@/assets/manuals/marketing/05-marketing-activity-log.png";
import shot06 from "@/assets/manuals/marketing/06-plan-vs-actual.png";

const SECTIONS: ManualSection[] = [
  { id: "intro",       label: "Your job in M-PACT" },
  { id: "home-base",   label: "Your AIP home base" },
  { id: "setup-grid",  label: "First-time setup — Annual Commitment Grid" },
  { id: "calendar",    label: "Building the Annual Activity Calendar" },
  { id: "hooks",       label: "Approving outreach hooks" },
  { id: "daily-log",   label: "Daily activity log" },
  { id: "scoreboard",  label: "Reading your Plan vs Actual" },
  { id: "handoffs",    label: "Where you hand off and where you receive" },
  { id: "glossary",    label: "Glossary of M-PACT terms" },
  { id: "rhythm",      label: "Daily and weekly rhythm cheat sheet" },
];

const BLACK = "#0B0B0F";

export default function MarketingManual() {
  return (
    <ManualLayout
      role="Marketing"
      title="Attilius Operating Manual — Marketing"
      meta="Marketing role · v1.1 · June 2026"
      pdfHref="/manuals-pdf/marketing.pdf"
      sections={SECTIONS}
    >
      {/* ── Chapter 1 — Your job in M-PACT ───────────────────────────────── */}
      <ManualSectionHeading id="intro" num={1} title="Your job in M-PACT" />

      <ManualPara>
        Marketing owns the top of the funnel. You are judged on three things — pacing the
        Annual Commitment Grid month by month and quarter by quarter, generating MQLs that
        BDRs accept as qualified, and feeding hooks that AEs and BDRs can pull straight into
        outbound. Every Mi6 role maps to the M-PACT pillars. Marketing maps in this way.
      </ManualPara>

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M — Methodology.</strong> Feed the methodology with content that respects the 10 checkpoints. A campaign that does not surface budget context, authority, timeline, or decision criteria is a campaign that produces noise rather than MQLs.</span>,
          <span><strong style={{ color: BLACK }}>P — Process / Prioritization.</strong> Keep the calendar and the Annual Commitment Grid current so the funnel is predictable. When the grid is current, leadership can forecast. When it is stale, leadership guesses.</span>,
          <span><strong style={{ color: BLACK }}>A — Accountability.</strong> Log marketing activity daily. The 7-day trend in the activity log is your visible work. Three blank days in a row is a signal.</span>,
          <span><strong style={{ color: BLACK }}>C — Cadence / Coaching.</strong> Join the Friday MQL reconciliation review with BDR and Manager. Reconciliation is where claimed MQLs get matched against accepted MQLs, and where coaching happens on lead quality.</span>,
          <span><strong style={{ color: BLACK }}>T — Timing / Timeline management.</strong> Defend launch dates and event dates. The Next Big Launch countdown on the calendar is yours to protect. A pending hook 14 days before an event is escalated to Bryan K. Short, Founder and CEO.</span>,
        ]}
      />

      <ManualPara>
        Manufacturing is the only context that matters here. The grid, the hooks, the events —
        every one is built for manufacturing buyers (Plant, Operations, Supply Chain,
        Engineering, Quality, Purchasing).
      </ManualPara>

      {/* ── Chapter 2 — Home base ────────────────────────────────────────── */}
      <ManualSectionHeading id="home-base" num={2} title="Your AIP home base — the Marketing dashboard" />

      <ManualPara>
        Route — <code>/#/dashboards/marketing</code> (top nav: Marketing). This is where you
        land after login. Walk through every region in this order, every morning.
      </ManualPara>

      <ManualImage
        src={shot01}
        alt="Marketing dashboard"
        caption="Live screen: /dashboards/marketing"
      />

      <StepList
        steps={[
          <span><strong>Funnel Banner — MQL stage pills.</strong> The header strip shows live MQL counts by funnel stage from the production API. This is your first read: where do leads sit right now, and are they advancing or stalling?</span>,
          <span><strong>MQL Pacing Bar.</strong> The pacing bar tracks MQL volume against the quarterly target. The live counter shows actual MQLs vs the quarter target. If this bar is red, nothing else matters.</span>,
          <span><strong>Pipeline Reality strip — COMMIT · UPSIDE · OMITTED · WON.</strong> What your funnel actually produced downstream. A heavy OMITTED count with thin COMMIT means MQLs are landing but not progressing through qualification. COMMIT is the deals the team has committed to close this quarter. UPSIDE is possible deals not yet committed. OMITTED is pipeline excluded from the current forecast. WON is closed revenue. These are text labels — not colored pills.</span>,
          <span><strong>Today's Drivers row — touches, engagements, MQLs created today.</strong> Three counters that snap to today's date. Use this to confirm your activity log is in sync with what AIP recorded.</span>,
          <span><strong>Marketing Funnel.</strong> Touches → Engagements → MQLs → MQLs accepted by BDR → Meetings booked → Opportunities. Watch Engagements → MQL and MQL → Accepted — the two stages most likely to leak.</span>,
          <span><strong>Channel mix bars.</strong> Top channel is highlighted in red. If a channel suddenly drops, audit hooks first, then spend.</span>,
          <span><strong>MQL Reconciliation card — claimed vs accepted by BDR.</strong> The gap is reviewed every Friday. A persistent gap means lead quality is off, not lead volume.</span>,
          <span><strong>Active Hook Campaigns list.</strong> Hooks that are live and producing MQLs. Each entry shows the hook label, the campaign, and current MQL count.</span>,
          <span><strong>Upcoming Events column.</strong> The next 5 events from the calendar. Every event in this column should have all hooks marked Approved before launch week.</span>,
          <span><strong>MQL Plan 2026 grid.</strong> Quarterly and monthly target visible at a glance — Q1 = 120, Q2 = 150, Q3 = 160, Q4 = 170. Same target as the Annual Commitment Grid.</span>,
          <span><strong>MQL → SQL → Opportunity waterfall.</strong> Drop-off at each stage in absolute numbers. Use this to spot where the funnel is leaking by volume, not just by percentage.</span>,
          <span><strong>BDR Handoff Queue.</strong> Leads waiting for a BDR to claim or return. If the queue is growing day over day, raise it on Friday.</span>,
        ]}
      />

      {/* ── Chapter 3 — Annual Commitment Grid ───────────────────────────── */}
      <ManualSectionHeading id="setup-grid" num={3} title="First-time setup — building the Annual Commitment Grid" />

      <ManualPara>
        Route — <code>/#/marketing/plan</code> (top nav: PLAN). This is the foundation
        document for the marketing year. Every other surface in AIP reads from this grid.
      </ManualPara>

      <ManualImage
        src={shot02}
        alt="Annual Commitment Grid 2026"
        caption="Live screen: /marketing/plan"
      />

      <StepList
        steps={[
          <span>Open <code>/#/marketing/plan</code> from the top nav.</span>,
          <span>Confirm the year selector at top right reads 2026 (or your current planning year).</span>,
          <span>Set the four annual targets at the top of the grid. Annual MQL Target (e.g., 600), Annual SQL Target (e.g., 150), Annual Budget (e.g., $146K), and Cost per MQL Target (e.g., $243).</span>,
          <span>Walk down each Activity Type row — Trade Shows, Hosted Round Tables, Webinars and Web Events, In-Person Hosted Events, Email Campaigns, Direct Mail, Podcast Appearances, Sponsored Content, Paid Ads and Digital Media, Co-Marketing, Speaking Engagements, Other Outreach. For each row, enter Q1–Q4 quantities, set Target MQLs and SQLs, and enter Budget in USD.</span>,
          <span>The Annual Totals row at the bottom must reconcile to the top-of-grid annual targets within tolerance. If totals do not match, fix the rows, do not fix the top.</span>,
          <span>Click <strong>Save Plan</strong> (red button, top right). Verify the "Last saved" timestamp in the header has updated.</span>,
          <span>Open the Top-10 ABM accounts panel below the grid. For each account, confirm marketing campaign theme is set, the AE Owner is correct, and the MQL target is committed.</span>,
          <span>Open the BDR Alignment panel on the right. Confirm cadence support hooks per account theme are claimed and assigned.</span>,
          <span>Sanity-check the Sales Handoff stat trio at the bottom — MQL→SQL %, MQL→OPP %, Closed-won attribution %. These three are the conversion truth for the year.</span>,
        ]}
      />

      <Callout tone="red">
        <strong style={{ color: BLACK }}>Common gotchas.</strong> If the MQL→SQL % is left at the
        25% default but the row historically converts at 15%, your forecast will be inflated.
        Override per row. Budget rows that read $0 with a non-zero quantity mean the activity is
        internal (no spend). Mark $0 explicitly. Do not leave the cell blank.
      </Callout>

      {/* ── Chapter 4 — Calendar ─────────────────────────────────────────── */}
      <ManualSectionHeading id="calendar" num={4} title="Building the Annual Activity Calendar" />

      <ManualPara>
        Route — <code>/#/marketing/calendar</code> (top nav: CALENDAR). The calendar is the
        time axis for the grid. Every committed activity from the grid must appear here as an
        event.
      </ManualPara>

      <ManualImage
        src={shot03}
        alt="Annual Activity Calendar 2026"
        caption="Live screen: /marketing/calendar"
      />

      <StepList
        steps={[
          <span>Open <code>/#/marketing/calendar</code> from the top nav.</span>,
          <span>Stay on Plan View (default). Actual View and Variance View come into use later, after activity is logged.</span>,
          <span>Click <strong>+ Add Event</strong> (top right, red).</span>,
          <span>For each event enter event name, activity type (must match a Plan grid row), date or date range, location (or Virtual), budget, and ABM-account tag if account-specific.</span>,
          <span>Confirm the event chip appears on the right month band.</span>,
          <span>Repeat until counts match. The number of events per activity type must equal Q1 + Q2 + Q3 + Q4 from the Plan grid for that row.</span>,
          <span>Use the <strong>This Week</strong> upcoming-events sidebar daily.</span>,
          <span>Watch the <strong>Next Big Launch</strong> countdown. Escalate to Bryan K. Short, Founder and CEO, if days-out is under 14 and any hook for that event is still PENDING.</span>,
        ]}
      />

      <ManualPara>
        Filter chips at the top (All, Campaigns, Webinars, Content, Events, Paid) let you
        isolate one stream at a time. The All Events list at the bottom shows status badges
        (Planned, In Progress) and an Edit button per row.
      </ManualPara>

      {/* ── Chapter 5 — Hooks ────────────────────────────────────────────── */}
      <ManualSectionHeading id="hooks" num={5} title="Approving outreach hooks" />

      <ManualPara>
        Route — <code>/#/marketing/hooks</code> (top nav: HOOKS). Hooks are the message text
        BDRs and AEs send. Marketing holds approval authority. AEs cannot send a hook until
        you Approve it.
      </ManualPara>

      <ManualImage
        src={shot04}
        alt="Outreach Hook Approval"
        caption="Live screen: /marketing/hooks"
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 6 }}>
        5A. Reusable Library (top section, 12 hooks)
      </h3>
      <ManualPara>
        The Library is organized in three columns — Email Hooks, LinkedIn Hooks, Content Hooks.
        Each card shows its current state (Approved, Pending, Draft).
      </ManualPara>
      <StepList
        steps={[
          <span>Open a hook card. Read the draft.</span>,
          <span>Edit copy as needed. Keep manufacturing context (Plant, Operations, Supply Chain, Engineering, Quality, Purchasing).</span>,
          <span>Set status to Approved when ready.</span>,
          <span>Click <strong>Pull into Cadence</strong> to push the hook into BDR or AE outreach surfaces.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 18, fontWeight: 700, marginTop: 24, marginBottom: 6 }}>
        5B. Per-event approvals
      </h3>
      <ManualPara>
        For each calendar event, AIP auto-generates 3 hook templates — Invite Hook (pre-event
        outreach inviting the prospect), Participation Hook (during-event nudge, often used
        the day of), and Round Table Hook (follow-up positioning the prospect into the next
        conversation).
      </ManualPara>
      <StepList
        steps={[
          <span>Read each card.</span>,
          <span>Replace any <code>[topic_placeholder]</code> or <code>[bullet_description]</code> tokens with finished copy.</span>,
          <span>Click <strong>Approve</strong>.</span>,
        ]}
      />

      <Callout>
        <strong style={{ color: BLACK }}>Reminder.</strong> AEs cannot send a hook until you
        Approve it. Marketing holds approval authority on all outbound message copy. A PENDING
        hook 7 days before an event is a flag. A PENDING hook 14 days out is escalated to
        Bryan K. Short, Founder and CEO. The hook approval counter at the top of the page tracks
        your approval progress toward launch readiness.
      </Callout>

      {/* ── Chapter 6 — Daily log ────────────────────────────────────────── */}
      <ManualSectionHeading id="daily-log" num={6} title="Daily activity log" />

      <ManualPara>
        Route — <code>/#/log/marketing</code> (top nav: ACTIVITY LOG). This is your
        accountability surface. Five minutes at end of day, every day.
      </ManualPara>

      <ManualImage
        src={shot05}
        alt="Daily Marketing Log"
        caption="Live screen: /log/marketing"
      />

      <StepList
        steps={[
          <span>At end of every workday, open <code>/#/log/marketing</code>.</span>,
          <span>Confirm the date picker shows today.</span>,
          <span>Fill the six numeric fields — Emails Sent, Social Posts Published, Webinar Registrations Driven, MQLs Added, Content Pieces Shipped, Ad Spend ($).</span>,
          <span>Use Notes for context — what launched today, what stalled, anything blocking the calendar or the grid.</span>,
          <span>Click <strong>Save</strong>.</span>,
          <span>Glance at the 7-day Activity Trend table below the form. Three blank days in a row is a signal you are falling behind. Two blank days is a yellow flag.</span>,
          <span>Use the footer links to jump back to Calendar, Annual Plan, or Sales Hooks without leaving the keyboard.</span>,
        ]}
      />

      {/* ── Chapter 7 — Scoreboard ───────────────────────────────────────── */}
      <ManualSectionHeading id="scoreboard" num={7} title="Reading your scoreboard — Plan vs Actual" />

      <ManualPara>
        Route — <code>/#/plan-vs-actual</code> (linked from the Marketing dashboard footer,
        also on the top nav of the Manager view). This is the single number that tells you
        and your manager whether the year is tracking.
      </ManualPara>

      <ManualImage
        src={shot06}
        alt="Plan vs Actual Q2 2026"
        caption="Live screen: /plan-vs-actual"
      />

      <StepList
        steps={[
          <span>Open <code>/#/plan-vs-actual</code>.</span>,
          <span>Default view is Quarter. Toggle Month for tactical reads, Year for strategic reads.</span>,
          <span>Read the dark stat strip first — Overall Attainment, Closed Quarter, Weighted Pipeline, Gap to Plan. Top-left to bottom-right, in that order.</span>,
          <span>Read the four KPI cards — Quota Attainment, Pipeline Coverage, Deals Closed, and <strong>MQL Volume</strong> (your direct accountability card). Anything under 90% is "slightly behind". Under 80% is "behind plan". Under 60% is "significantly behind".</span>,
          <span>Scan the By-AE table. This shows who is converting your MQLs into closed-won and who is not. If an account executive is significantly behind, audit which campaigns sourced their leads — the question is whether the leads were qualified, not just whether the AE worked them.</span>,
          <span>Color rule. Green at 95% or above. Yellow 80–94%. Orange 60–79%. Red below 60%.</span>,
        ]}
      />

      {/* ── Chapter 8 — Handoffs ─────────────────────────────────────────── */}
      <ManualSectionHeading id="handoffs" num={8} title="Where you hand off and where you receive" />

      <ManualPara>
        Marketing is an interface role. You ship work to four downstream consumers and
        receive work from four upstream producers.
      </ManualPara>

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 8 }}>
        You hand off to
      </h3>
      <ManualTable
        headers={["Recipient", "What you send", "When"]}
        rows={[
          ["BDR", "Approved hooks plus MQLs in the handoff queue", "Daily"],
          ["AE", "Per-account ABM campaign theme plus hook copy with placeholders", "Weekly"],
          ["Manager", "MQL volume plus reconciliation gap", "Friday review"],
          ["Executive", "Quarterly funnel attainment summary", "End of quarter"],
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 24, marginBottom: 8 }}>
        You receive from
      </h3>
      <ManualTable
        headers={["Source", "What", "When"]}
        rows={[
          ["BDR", "Returned MQLs plus acceptance rate", "Real-time, audited Friday"],
          ["AE", "Top-10 account names plus champion intel", "Weekly"],
          ["Manager", "Reallocations to plan", "Monthly"],
          ["Executive", "Strategic theme priorities plus budget shifts", "Quarterly"],
        ]}
      />

      {/* ── Chapter 9 — Glossary ─────────────────────────────────────────── */}
      <ManualSectionHeading id="glossary" num={9} title="Glossary of M-PACT terms" />

      <BulletList
        items={[
          <span><strong style={{ color: BLACK }}>M-PACT</strong> — the Mi6 operating model. M = Methodology. P = Process / Prioritization. A = Accountability. C = Cadence / Coaching. T = Timing / Timeline management.</span>,
          <span><strong style={{ color: BLACK }}>Checkpoint (CP1–CP10)</strong> — the 10 qualification checkpoints Mi6 enforces on every opportunity. CP1 Account Profile, CP2 Compelling Pain, CP3 Budget, CP4 Timeline, CP5 Strategic Fit, CP6 Solution Fit, CP7 Competitive Landscape, CP8 Decision Process, CP9 Risk and Stakeholder Alignment, CP10 Commitment to Buy. Marketing content should create entry-points for as many of these as possible.</span>,
          <span><strong style={{ color: BLACK }}>Compelling Reason to Buy</strong> — the buyer-confirmed trigger that makes this purchase necessary now. Not a vendor-asserted benefit. Lives under CP2 Compelling Pain. Your campaigns surface Compelling Reasons to Buy — not generic pain points.</span>,
          <span><strong style={{ color: BLACK }}>MQL</strong> — Marketing Qualified Lead. A lead Marketing has surfaced and believes is qualified for BDR follow-up.</span>,
          <span><strong style={{ color: BLACK }}>SQL</strong> — Sales Qualified Lead. A lead a BDR has accepted and qualified into a sales conversation.</span>,
          <span><strong style={{ color: BLACK }}>OPP</strong> — Opportunity. A qualified deal with a project, budget context, and a date.</span>,
          <span><strong style={{ color: BLACK }}>Closed-Won</strong> — a signed deal.</span>,
          <span><strong style={{ color: BLACK }}>COMMIT</strong> — the forecast bucket for deals the account executive has committed to close this quarter. The most defensible forward-looking bucket in the pipeline.</span>,
          <span><strong style={{ color: BLACK }}>UPSIDE</strong> — possible deals, not committed. Pipeline that could close this quarter but has not been committed by the account executive.</span>,
          <span><strong style={{ color: BLACK }}>OMITTED</strong> — pipeline excluded from the current forecast.</span>,
          <span><strong style={{ color: BLACK }}>WON</strong> — closed-won revenue. The Pipeline Reality strip shows WON alongside COMMIT, UPSIDE, and OMITTED so you can see the full conversion story in one band.</span>,
          <span><strong style={{ color: BLACK }}>Hook</strong> — a piece of message text, written for a specific event or campaign. Three types per event — Invite Hook, Participation Hook, Round Table Hook.</span>,
          <span><strong style={{ color: BLACK }}>Cadence</strong> — the outbound surface where hooks live once pulled. BDRs and AEs pull approved hooks into their cadences.</span>,
          <span><strong style={{ color: BLACK }}>Annual Commitment Grid</strong> — the year plan at <code>/#/marketing/plan</code>. Activity Type rows × quarters, with MQL targets, SQL targets, and budget per row.</span>,
          <span><strong style={{ color: BLACK }}>Opportunity DNA / Helix</strong> — the AIP visual decision system. <code>/#/opportunity-dna</code> is the per-account diagnostic that maps where a deal sits across the 10 checkpoints. <code>/#/helix</code> is the territory-level aggregate view. Marketing reads the per-account diagnostic to understand which checkpoint gaps content campaigns should address.</span>,
          <span><strong style={{ color: BLACK }}>Firm / formal timeline</strong> — the buyer-stated date a project must close by. Always lives under CP4 Timeline. Never a standalone field.</span>,
          <span><strong style={{ color: BLACK }}>ABM lift</strong> — the marketing alignment with the Top-10 account list. Each Top-10 account has a campaign theme tied to its AE plan.</span>,
          <span><strong style={{ color: BLACK }}>Reconciliation</strong> — the Friday review of claimed MQLs versus accepted MQLs. Marketing, BDR, and Manager attend.</span>,
        ]}
      />

      <Callout>
        <strong style={{ color: BLACK }}>Closing note.</strong> The helix is the diagnostic.
        The path is the play.
      </Callout>

      {/* ── Chapter 10 — Rhythm ──────────────────────────────────────────── */}
      <ManualSectionHeading id="rhythm" num={10} title="Daily and weekly rhythm cheat sheet" />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 12, marginBottom: 6 }}>
        Daily
      </h3>
      <StepList
        steps={[
          <span>Open the Marketing dashboard at <code>/#/dashboards/marketing</code>.</span>,
          <span>Glance at MQL pacing bar — is the quarter on track?</span>,
          <span>Read the Pipeline Reality strip — COMMIT · UPSIDE · OMITTED · WON. Is pipeline advancing or stalling?</span>,
          <span>Review any PENDING hooks and approve or revise.</span>,
          <span>End of day, log activity at <code>/#/log/marketing</code>.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Weekly
      </h3>
      <BulletList
        items={[
          <span><strong>Monday.</strong> Open the Annual Calendar This Week panel. Confirm every event this week has hooks approved.</span>,
          <span><strong>Mid-week.</strong> Confirm the Next Big Launch countdown is green. If days-out is under 14 and any hook is PENDING, escalate to Bryan K. Short, Founder and CEO.</span>,
          <span><strong>Friday.</strong> MQL reconciliation with BDR. Plan-vs-Actual review with Manager.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Monthly
      </h3>
      <BulletList
        items={[
          <span>Review Plan-vs-Actual at month-end on <code>/#/plan-vs-actual</code>.</span>,
          <span>Adjust the calendar for next month based on variance.</span>,
          <span>Reset the hook library — retire hooks tied to past events, surface drafts for upcoming events.</span>,
        ]}
      />

      <h3 style={{ color: BLACK, fontSize: 16, fontWeight: 700, marginTop: 16, marginBottom: 6 }}>
        Quarterly
      </h3>
      <BulletList
        items={[
          <span>Full Annual Commitment Grid review.</span>,
          <span>Re-balance budget allocation across rows based on cost per MQL by row.</span>,
          <span>Publish the quarter recap to Executive.</span>,
        ]}
      />
    </ManualLayout>
  );
}
